import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  TooltipProps,
} from 'recharts';
import { cn } from '../../lib/utils';
import { useChartPalette } from './chartColors';

export interface TailwindBarChartSeries {
  dataKey: string;
  label?: string;
  color?: string;
  stackId?: string; // For stacked bar charts
  radius?: number | [number, number, number, number];
}

export interface TailwindBarChartBrushConfig {
  enabled?: boolean;        // true to force-show, false to force-hide
  minDataPoints?: number;   // auto-show when data.length > this (default: never auto)
  height?: number;          // default 24
  travellerWidth?: number;  // default 8
  stroke?: string;          // default = first series color
}

export interface TailwindBarChartProps {
  data: any[];
  series: TailwindBarChartSeries[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  width?: number | string;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  layout?: 'horizontal' | 'vertical';
  stacked?: boolean;
  className?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  customTooltip?: React.ComponentType<TooltipProps<any, any>>;
  yAxisTickFormatter?: (value: any) => string;
  xAxisTickFormatter?: (value: any) => string;
  xAxisTicks?: (string | number)[];
  xAxisTickFontSize?: number;
  yAxisTickFontSize?: number;
  tooltipValueFormatter?: (value: any, name?: string) => React.ReactNode | [React.ReactNode, React.ReactNode];
  brush?: TailwindBarChartBrushConfig;
}

export default function TailwindBarChart({
  data,
  series,
  xAxisKey = 'name',
  xAxisLabel,
  yAxisLabel,
  height = 400,
  width = '100%',
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  layout = 'horizontal',
  stacked = false,
  className,
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
  customTooltip,
  yAxisTickFormatter,
  xAxisTickFormatter,
  xAxisTicks,
  xAxisTickFontSize = 12,
  yAxisTickFontSize = 12,
  tooltipValueFormatter,
  brush,
}: TailwindBarChartProps) {
  const palette = useChartPalette();
  const getColor = (index: number) => palette[index % palette.length];
  const stackId = stacked ? 'stack' : undefined;

  const showBrush =
    brush?.enabled === true ||
    (brush?.enabled !== false &&
      brush?.minDataPoints != null &&
      data.length > brush.minDataPoints);

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width={width} height={height}>
        <BarChart
          data={data}
          margin={margin}
          layout={layout}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
          )}
          {layout === 'horizontal' ? (
            <>
              <XAxis
                dataKey={xAxisKey}
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
                tick={{ fill: '#64748b', fontSize: xAxisTickFontSize }}
                stroke="#cbd5e1"
                ticks={xAxisTicks}
                tickFormatter={xAxisTickFormatter}
              />
              <YAxis
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
                tick={{ fill: '#64748b', fontSize: yAxisTickFontSize }}
                stroke="#cbd5e1"
                tickFormatter={yAxisTickFormatter}
              />
            </>
          ) : (
            <>
              <XAxis
                type="number"
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
                tick={{ fill: '#64748b', fontSize: xAxisTickFontSize }}
                stroke="#cbd5e1"
                tickFormatter={xAxisTickFormatter ?? yAxisTickFormatter}
              />
              <YAxis
                type="category"
                dataKey={xAxisKey}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
                tick={{ fill: '#64748b', fontSize: yAxisTickFontSize }}
                stroke="#cbd5e1"
              />
            </>
          )}
          {showTooltip && (
            customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                formatter={tooltipValueFormatter as any}
              />
            )
          )}
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
            />
          )}
          {series.map((s, index) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.label || s.dataKey}
              fill={s.color || getColor(index)}
              stackId={s.stackId || stackId}
              radius={s.radius}
            />
          ))}
          {showBrush && (
            <Brush
              dataKey={xAxisKey}
              height={brush?.height ?? 24}
              travellerWidth={brush?.travellerWidth ?? 8}
              stroke={brush?.stroke ?? (series[0]?.color || getColor(0))}
              tickFormatter={xAxisTickFormatter}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
